import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Table, Button, Badge, EmptyState, LoadingSkeleton, Pagination, FilterBar, FilterSelect, FilterSearch } from './ui';
import { UserIcon, PlusIcon, PencilIcon, TrashIcon, ArrowUturnLeftIcon, ArchiveBoxIcon, KeyIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../utils/roles';
import AddAccountModal from './accounts/AddAccountModal';

const MANAGE_ROLES = ['administrator', 'principal'];
const STATUS = {
  active: { label: 'Active', badge: 'success' },
  on_leave: { label: 'On leave', badge: 'warning' },
  disabled: { label: 'Disabled', badge: 'danger' },
};

function Teachers({ token, user }) {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [showRemoved, setShowRemoved] = useState(false);
  // Held in state (not built inline) so AddAccountModal's preset stays stable across renders
  const [loginPreset, setLoginPreset] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [search, setSearch] = useState('');
  const pageSize = 20;
  const canManage = hasAnyRole(user, MANAGE_ROLES);

  const openCreateLogin = (teacher) => {
    setLoginPreset({ role: 'teacher', teacher_id: teacher.id, full_name: teacher.full_name, email: teacher.email || '', phone: teacher.phone || '' });
  };

  const fetchTeachers= useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/teachers${showRemoved ? '?status=removed' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setTeachers(await response.json());
      } else {
        setError('Failed to load teachers');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token, showRemoved]);

  useEffect(() => { fetchTeachers(); }, [fetchTeachers]);
  useEffect(() => { setPage(1); }, [showRemoved, statusFilter, subjectFilter, search]);

  const handleDelete = async (teacher) => {
    if (!window.confirm(`Remove ${teacher.full_name} from the teachers directory? This can be undone from "Removed Teachers".`)) return;
    try {
      const response = await fetch(`/api/v1/teachers/${teacher.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setTeachers((prev) => prev.filter((t) => t.id !== teacher.id));
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to remove teacher');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleRestore = async (teacher) => {
    try {
      const response = await fetch(`/api/v1/teachers/${teacher.id}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setTeachers((prev) => prev.filter((t) => t.id !== teacher.id));
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to restore teacher');
      }
    } catch {
      setError('Network error');
    }
  };

  const term = search.trim().toLowerCase();
  const subjectOptions = [...new Set(teachers.flatMap((t) => t.subjects || []).filter(Boolean))].sort();
  const filtersActive = Boolean(statusFilter || subjectFilter || term);
  const clearFilters = () => { setStatusFilter(''); setSubjectFilter(''); setSearch(''); };

  const sorted = teachers
    .filter((t) => !statusFilter || (t.status || 'active') === statusFilter)
    .filter((t) => !subjectFilter || (t.subjects || []).includes(subjectFilter))
    .filter((t) => !term || [t.full_name, t.staff_id, t.email].some((value) => value?.toLowerCase().includes(term)))
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const columns = [
    { header: 'Staff ID', render: (row) => <strong>{row.staff_id}</strong> },
    { header: 'Full Name', accessor: 'full_name' },
    { header: 'Subjects', render: (row) => row.subjects?.join(', ') || '—' },
    { header: 'Classes', render: (row) => row.classes_assigned?.join(', ') || '—' },
    { header: 'Phone', render: (row) => row.phone || '—' },
    { header: 'Email', render: (row) => row.email || '—' },
    {
      header: 'Status',
      render: (row) => {
        const status = STATUS[row.status] || STATUS.active;
        return <Badge variant={status.badge}>{status.label}</Badge>;
      },
    },
  ];

  if (canManage && !showRemoved) {
    columns.push({
      header: 'Login',
      render: (row) => (row.has_login ? (
        <Badge variant="success">Has login</Badge>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => openCreateLogin(row)}>
          <KeyIcon style={{ width: 14, height: 14 }} />
          Create login
        </Button>
      )),
    });
  }

  if (showRemoved) {
    columns.push({
      header: 'Actions',
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => handleRestore(row)}>
          <ArrowUturnLeftIcon style={{ width: 14, height: 14 }} />
          Restore
        </Button>
      ),
    });
  } else if (canManage) {
    columns.push({
      header: 'Actions',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/teachers/edit/${row.id}`)}>
            <PencilIcon style={{ width: 14, height: 14 }} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row)}>
            <TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} />
          </Button>
        </div>
      ),
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>
          {showRemoved ? 'Removed Teachers' : 'Teachers Directory'}
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {canManage && (
            <Button variant="secondary" size="sm" onClick={() => setShowRemoved((v) => !v)}>
              <ArchiveBoxIcon style={{ width: 16, height: 16 }} />
              {showRemoved ? 'Back to Directory' : 'Removed Teachers'}
            </Button>
          )}
          {!showRemoved && canManage && (
            <Link to="/teachers/new">
              <Button variant="primary" size="sm">
                <PlusIcon style={{ width: 16, height: 16 }} />
                Add Teacher
              </Button>
            </Link>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!showRemoved && (
        <FilterBar shown={sorted.length} total={teachers.length} onClear={filtersActive ? clearFilters : undefined}>
          <FilterSearch value={search} onChange={setSearch} placeholder="Name, staff ID or email" />
          <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={Object.entries(STATUS).map(([value, item]) => ({ value, label: item.label }))} allLabel="Any status" />
          <FilterSelect label="Subject" value={subjectFilter} onChange={setSubjectFilter} options={subjectOptions} allLabel="All subjects" />
        </FilterBar>
      )}

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={8} />
        ) : teachers.length === 0 ? (
          <EmptyState
            title={showRemoved ? 'No removed teachers' : 'No teachers yet'}
            message={showRemoved ? 'Removed teachers will appear here and can be restored.' : (canManage ? 'Add your first teacher to get started.' : 'No teachers to show yet.')}
            icon={<UserIcon />}
            action={!showRemoved && canManage && <Link to="/teachers/new"><Button variant="primary" size="sm"><PlusIcon style={{ width: 16, height: 16 }} />Add Teacher</Button></Link>}
          />
        ) : (
          <>
            <Table columns={columns} data={paginated} emptyMessage="No teachers match these filters." />
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </Card>

      <AddAccountModal
        token={token}
        isOpen={Boolean(loginPreset)}
        preset={loginPreset}
        onClose={() => setLoginPreset(null)}
        onCreated={(account) => setTeachers((prev) => prev.map((t) => (t.id === loginPreset.teacher_id ? { ...t, has_login: true, user_id: account.id } : t)))}
      />
    </div>
  );
}

export default Teachers;
